import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormHelperText, Grid, Divider, Stack, Skeleton, MenuItem } from '@mui/material';
import { styles } from './styles';
import { useNavigate } from 'react-router-dom';
import TextField from '../../../components/common/InputField/TextField';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import { useNewLocationActionMutation, useModifyLocationActionMutation } from '../../../api/locations';

const LocationDetailsTab = ({ isEditMode, locationDetails, isLoading }) => {
    const navigate = useNavigate();
    const { status = [], locationInstance = {} } = locationDetails || {};
    const { enqueueSnackbar } = useSnackbar();
    const [newLocationAction, { isLoading: isNewLoading }] = useNewLocationActionMutation();
    const [modifyLocationAction, { isLoading: isModifyLoading }] = useModifyLocationActionMutation();

    const loading = isNewLoading || isModifyLoading;

    const handleCustomDetails = async (details) => {
        try {
            const bodyFormData = new FormData();
            for (const key in details) {
                bodyFormData.append(key, details[key]);
            }
            let response;
            if (isEditMode) {
                response = await modifyLocationAction(bodyFormData).unwrap();
            } else {
                response = await newLocationAction(bodyFormData).unwrap();
            }
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response.message, { variant: 'success' });
            navigate('/settings/locations');
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const formik = useFormik({
        initialValues: {
            id: locationInstance?.id ?? '',
            name: locationInstance?.name ?? '',
            status: locationInstance?.status ?? 'Active',
        },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            name: Yup.string()
                .max(255)
                .required('Name is required')
                .matches(
                    /^[A-Za-z0-9 :,\-]+$/,
                    'Name can contain only letters, numbers, spaces, commas, colon, and hyphens',
                ),
            status: Yup.string().max(255).required('Status is required'),
        }),
        onSubmit: async (values, helpers) => {
            try {
                handleCustomDetails({ ...values, name: values.name?.trim().replace(/\s+/g, ' ') });
                //helpers.resetForm();
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({
                    submit: err.message,
                });
                helpers.setSubmitting(false);
            }
        },
    });
    if (isLoading) {
        return (
            <Box sx={{ py: 4 }}>
                <Skeleton height={42} />
                <Skeleton />
                <Skeleton />
            </Box>
        );
    }
    return (
        <>
            <Grid container spacing={4} sx={{ py: 4 }}>
                <Grid item md={12} xs={12}>
                    <form onSubmit={formik.handleSubmit}>
                        <div>
                            <Grid container columnSpacing={6} rowSpacing={3}>
                                <Grid item xs={4}>
                                    <TextField
                                        error={Boolean(formik.touched.name && formik.errors.name)}
                                        fullWidth
                                        helperText={formik.touched.name && formik.errors.name}
                                        label="Name"
                                        name="name"
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        value={formik.values.name}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        error={Boolean(formik.touched.status && formik.errors.status)}
                                        label="Status"
                                        name={'status'}
                                        helperText={formik.touched.status && formik.errors.status}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        select
                                        children={status.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {status}
                                            </MenuItem>
                                        ))}
                                        value={formik.values.status}
                                    />
                                </Grid>
                                {formik.errors.submit && (
                                    <Grid item xs={12}>
                                        <FormHelperText error>{formik.errors.submit}</FormHelperText>
                                    </Grid>
                                )}
                                <Grid item xs={12} sx={styles.dividerWrapper}>
                                    <Divider flexItem orientation="horizontal" sx={styles.divider} />{' '}
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction="row" justifyContent="end">
                                        <LoadingButton
                                            loading={loading}
                                            color="primary"
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                        >
                                            {isEditMode ? 'Update' : 'Create'}
                                        </LoadingButton>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </div>
                    </form>
                </Grid>
            </Grid>
        </>
    );
};

export default LocationDetailsTab;
